/**
 * PATH: packages/ui/src/live-toasts/useNotificationToasts.ts
 * FILE_ID: UI-LIVE-TOASTS-NOTIFICATION-DIFF-001
 * PURPOSE: Turn the bell's polled list into live toasts: rows that arrive
 *          after the page loaded, unread and recent. The host may map a row to
 *          its own toast (Clock in, Approve); otherwise it gets title + Open.
 * INVARIANT: reads the list the bell already polls. Never fetches; a second
 *          poller would undo the duplicate-call fix (#1307).
 * LANDMINE: Django upserts some rows by unique_key (same id, new updated_at),
 *          so the toast key is id + timestamp. The schedule notice also lands
 *          in the workforce feed, so equal title + message within two minutes
 *          toasts once.
 */
import { useEffect, useRef } from 'react'
import { isVersionUpdate, type NotificationItem } from '../components/notification-center/types'
import { pushLiveToast, type LiveToastSpec, type LiveToastTone } from './liveToastStore'

/** undefined: use the default toast. null: no toast for this row. */
export type NotificationToastResolver = (item: NotificationItem) => LiveToastSpec | null | undefined

/** Older rows are history, not news. */
export const NOTIFICATION_RECENT_MS = 10 * 60_000
/** Server and browser clocks disagree a little; the poll runs every 15s. */
export const MOUNT_SKEW_MS = 30_000
export const CONTENT_DEDUPE_MS = 2 * 60_000

const TONE: Record<string, LiveToastTone> = {
  critical: 'error',
  warning: 'warning',
  success: 'success',
  info: 'info',
}

function stamp(item: NotificationItem): string {
  return item.updated_at || item.created_at || ''
}

export function notificationKey(item: NotificationItem): string {
  return `${item.id}:${stamp(item)}`
}

export function notificationContentKey(item: NotificationItem): string {
  return `${(item.title || '').trim().toLowerCase()}|${(item.message || '').trim().toLowerCase()}`
}

/**
 * Pure: rows worth a toast, given the keys already handled and the mount time.
 * Product-update rows stay in the bell; the version check owns that prompt.
 */
export function freshNotifications(
  items: readonly NotificationItem[],
  seenKeys: ReadonlySet<string>,
  mountedAt: number,
  now: number,
): NotificationItem[] {
  return items.filter((item) => {
    if (item.is_read || item.read_at || seenKeys.has(notificationKey(item)) || isVersionUpdate(item)) return false
    const at = Date.parse(stamp(item))
    return Number.isFinite(at) && at >= mountedAt - MOUNT_SKEW_MS && now - at <= NOTIFICATION_RECENT_MS
  })
}

/** Pure: the toast a row gets when the host has nothing better. */
export function defaultNotificationToast(item: NotificationItem): LiveToastSpec {
  return {
    id: `notification:${notificationKey(item)}`,
    title: item.title || 'New notification',
    body: item.message || undefined,
    tone: TONE[item.severity] ?? 'info',
    icon: 'bell',
    actions: item.action_url
      ? [{ label: item.action_label || 'Open', kind: 'primary', href: item.action_url }]
      : [],
  }
}

export function useNotificationToasts(
  items: readonly NotificationItem[],
  resolve?: NotificationToastResolver,
  enabled = true,
): void {
  const mountedAt = useRef(Date.now())
  const seen = useRef(new Set<string>())
  const recentContent = useRef(new Map<string, number>())
  const resolveRef = useRef(resolve)
  resolveRef.current = resolve

  useEffect(() => {
    if (!enabled) return
    const now = Date.now()
    for (const item of freshNotifications(items, seen.current, mountedAt.current, now)) {
      const content = notificationContentKey(item)
      const last = recentContent.current.get(content)
      recentContent.current.set(content, now)
      if (last !== undefined && now - last < CONTENT_DEDUPE_MS) continue
      const resolved = resolveRef.current?.(item)
      const spec = resolved === undefined ? defaultNotificationToast(item) : resolved
      if (spec) pushLiveToast(spec)
    }
    for (const item of items) seen.current.add(notificationKey(item))
  }, [items, enabled])
}
